import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from 'src/components/modals/comps';
import { Poppins, Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import CSelect from 'src/components/form/Select';
import { AutoForm } from './AutoForm';
import { FormField } from './FormField';
import { WidthPreset } from './contract';
import { PRESET_FRACTION, PRESET_LABEL } from './sizing';
import { EditableEnvelope, FeComponentDescriptor } from './registry';

// App-standard modal layout (mirrors AddUserModal et al.): padding:0 on the
// styled modal, a 30px top/bottom spacer, an h-padding body, an m-title, and a
// two-column .btns grid. The component's own form is inserted as the body, with
// a shared Width preset control appended when the component is resizable.
const StyledModal = styled(Modal)`
  width: 560px;
  padding: 0;

  .btns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
  }
`;

type Opt = { label: string; value: string };

export const SettingsModal: React.FC<{
  descriptor: FeComponentDescriptor;
  initial: EditableEnvelope;
  onClose: () => void;
  onSave: (env: EditableEnvelope) => void;
}> = ({ descriptor, initial, onClose, onSave }) => {
  const [value, setValue] = useState<EditableEnvelope>(initial);
  const Form = descriptor.SettingsForm;

  const showWidth = descriptor.sizing.presets.length > 1;
  const presetOptions: Opt[] = descriptor.sizing.presets.map((p) => ({ value: p, label: PRESET_LABEL[p] ?? p }));
  // Surface a drag-produced 'custom' width so the select has a matching value.
  const widthOptions: Opt[] =
    value.layout.widthPreset === 'custom'
      ? [{ value: 'custom', label: `Custom (${Math.round(value.layout.widthFraction * 100)}%)` }, ...presetOptions]
      : presetOptions;

  return (
    <StyledModal isOpen onRequestClose={() => onClose()}>
      <Spacer $px={30} />
      <div className="h-padding">
        <Poppins className="m-title" px={28}>
          {descriptor.label}
        </Poppins>

        {Form ? (
          <Form descriptor={descriptor} value={value} onChange={setValue} />
        ) : (
          <AutoForm
            schema={descriptor.configSchema}
            value={value.config}
            onChange={(config) => setValue((v) => ({ ...v, config }))}
          />
        )}

        {showWidth && (
          <FormField label="Width">
            <CSelect
              inputId="rc-width"
              name="rc-width"
              options={widthOptions}
              value={widthOptions.find((o) => o.value === value.layout.widthPreset) ?? null}
              onChange={(o: Opt) => {
                const preset = o.value as WidthPreset;
                const fraction = PRESET_FRACTION[preset];
                if (fraction == null) return; // 'custom' is display-only
                setValue((v) => ({ ...v, layout: { ...v.layout, widthPreset: preset, widthFraction: fraction } }));
              }}
            />
          </FormField>
        )}

        <Spacer $px={40} />
        <div className="btns">
          <Button secondary onClick={() => onClose()}>
            CANCEL
          </Button>
          <Button primary onClick={() => onSave(value)}>
            SAVE
          </Button>
        </div>
      </div>
      <Spacer $px={30} />
    </StyledModal>
  );
};
